'use client';

import { Button, Card, CardHeader, CardTitle, CardContent } from '../';
import { useI18n } from '../';

interface CFXLoginProps {
  returnUrl?: string;
  error?: string;
  onLogin?: () => void;
  title?: string;
  description?: string;
}

export function CFXLogin({ 
  returnUrl, 
  error, 
  onLogin,
  title,
  description 
}: CFXLoginProps) {
  const { t } = useI18n();

  const handleLogin = () => {
    if (onLogin) {
      onLogin();
      return;
    }

    const url = returnUrl 
      ? `/api/auth/cfx/login?redirect=${encodeURIComponent(returnUrl)}`
      : '/api/auth/cfx/login';
    
    window.location.href = url;
  };

  const getErrorMessage = (errorCode: string) => {
    switch (errorCode) {
      case 'oauth_denied':
        return t.auth?.errorOAuthDenied || 'Authentication was cancelled';
      case 'missing_code':
        return t.auth?.errorMissingCode || 'Authentication failed - missing authorization code';
      case 'auth_failed':
        return t.auth?.errorAuthFailed || 'Authentication failed - please try again';
      default:
        return t.auth?.errorGeneral || 'An error occurred during authentication';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">
              {title || t.auth?.cfxLogin || 'Login with CFX.re'}
            </CardTitle>
            {description && (
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                {description}
              </p>
            )}
          </CardHeader>
          
          <CardContent className="space-y-6">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700 dark:text-red-200">
                      {getErrorMessage(error)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      {t.auth?.cfxLoginInfo || 'CFX.re Authentication'}
                    </h3>
                    <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                      {t.auth?.cfxLoginDescription || 'You will be redirected to CFX.re to authenticate with your FiveM account.'}
                    </p>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleLogin}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.374 0 0 5.373 0 12s5.374 12 12 12 12-5.373 12-12S18.626 0 12 0zm5.568 8.16c-.169 1.858-.896 3.433-2.174 4.71C13.982 14.282 12.227 15 10.24 15c-1.986 0-3.74-.718-5.152-2.13C3.606 11.487 2.88 9.913 2.88 7.84c0-2.073.726-3.647 2.208-5.04C6.5 1.407 8.254.69 10.24.69c1.987 0 3.741.717 5.154 2.11 1.421 1.393 2.174 2.967 2.174 5.04 0 .144-.003.287-.01.43z"/>
                </svg>
                <span>{t.auth?.continueWithCFX || 'Continue with CFX.re'}</span>
              </Button>

              <div className="text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t.auth?.cfxLoginFooter || 'Secure authentication powered by FiveM/CFX.re'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default CFXLogin;
